import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Clock } from 'lucide-react'
import usePopover, { panelStyle } from './usePopover'
import { kit } from './strings'
import './TimePicker.css'

const pad = (n) => String(n).padStart(2, '0')
const HOURS = Array.from({ length: 24 }, (_, hour) => pad(hour))

/**
 * What someone typed, as `HH:mm` — or null if it cannot be one.
 *
 * A SEPARATOR IS BELIEVED WHEN THERE IS ONE. Reading `9:5` as its digits gives `95`, which
 * is not an hour, so it was refused — while the person typing it plainly meant five past
 * nine. With a colon each side is read for itself; without one the digits are positional,
 * so `1437` and `937` both work. Two digits or fewer are an hour on its own (`9` → `09:00`),
 * the one thing a half-typed time can unambiguously mean.
 */
function parseTime(text) {
  const raw = String(text ?? '').trim()
  if (!raw) return null

  let hour
  let minute

  const split = /^(\d{1,2})\s*[:.\s]\s*(\d{0,2})$/.exec(raw)
  if (split) {
    hour = Number(split[1])
    minute = split[2] === '' ? 0 : Number(split[2])
  } else {
    const digits = raw.replace(/\D/g, '')
    if (!digits || digits.length > 4) return null
    if (digits.length <= 2) {
      hour = Number(digits)
      minute = 0
    } else {
      hour = Number(digits.slice(0, digits.length - 2))
      minute = Number(digits.slice(-2))
    }
  }

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null
  if (hour > 23 || minute > 59) return null
  return `${pad(hour)}:${pad(minute)}`
}

/**
 * A clock time: typed, or picked.
 *
 * NOT `<input type="time">`. The native control hands the whole field to the OS: it prints
 * a US `--:-- --` mask on an Arabic-first screen, draws a second clock glyph beside the one
 * the field already has, and opens a browser popup that answers to none of the tokens
 * everything around it is built from. `DateRangeField` reached the same conclusion about
 * native date inputs, for the same reasons.
 *
 * TYPING IS THE PRIMARY WAY IN, and the grids are the shortcut. A panel alone could only
 * ever offer the minutes it listed — five past, ten past — so 14:37 was unreachable, and
 * a scheduling field that cannot express a third of an hour is a field with an opinion
 * about when you are allowed to send things. The box takes any minute; the grids stay
 * because most sends really are on the hour or the quarter, and two taps beat six
 * keystrokes.
 *
 * The hour grid holds all 24. The minute grid holds one every `step`, which is a set of
 * SHORTCUTS rather than the range of legal values — anything 00–59 can be typed.
 *
 * @param {string} value  `HH:mm`, or empty
 * @param {(value: string) => void} onChange
 * @param {'field'|'bare'} [variant]  `bare` drops the shell, for a caller that owns one
 * @param {number} [step]  minutes between the offered shortcuts
 */
export default function TimePicker({
  id,
  value = '',
  onChange,
  variant = 'field',
  step = 5,
  disabled = false,
  invalid = false,
  placeholder = 'HH:mm',
  className = '',
  ...rest
}) {
  /* HANGS FROM THE TRIGGER'S END EDGE, so the panel opens back across the form rather
     than away from it. This control is the last thing in its field, which puts it hard
     against the form's inline-end — anchored the other way the panel spilled past the
     dialog and read as belonging to nothing. */
  const { open, setOpen, close, pos, rootRef, panelRef, id: panelId } = usePopover({
    align: 'end',
  })

  const inputRef = useRef(null)
  /* WHAT IS IN THE BOX, which is not the same thing as the value while someone is partway
     through typing one: `14:3` is not a time, and pushing it upward would either be
     rejected or land as 14:03 and move the caret. The draft is local until it parses. */
  const [draft, setDraft] = useState(value)
  const [editing, setEditing] = useState(false)

  /* A value set from outside — the grids, a reset, a form loading a record — wins, but
     only while the box is not being typed into. */
  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  const minutes = Array.from({ length: Math.ceil(60 / step) }, (_, i) => pad(i * step))
  const [hour = '', minute = ''] = value ? value.split(':') : []

  /* Choosing one half fills the other in, so a single tap is already a usable time:
     an hour on its own means the top of that hour. */
  const emit = (nextHour, nextMinute) => onChange?.(`${nextHour}:${nextMinute}`)

  const onType = (event) => {
    const next = event.target.value
    setDraft(next)
    /* Emitted as soon as it reads as a time, so the rest of the form sees the value
       without waiting for a blur — but the box keeps showing exactly what was typed. */
    const parsed = parseTime(next)
    if (parsed) onChange?.(parsed)
    else if (!next.trim()) onChange?.('')
  }

  /* Leaving tidies up: `9` becomes 09:00, and something that never became a time gives
     the field back rather than clearing it. */
  const commit = () => {
    setEditing(false)
    const parsed = parseTime(draft)
    if (parsed) {
      setDraft(parsed)
      onChange?.(parsed)
    } else if (!String(draft).trim()) {
      setDraft('')
      onChange?.('')
    } else {
      setDraft(value)
    }
  }

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commit()
      close()
    } else if (event.key === 'Escape' && open) {
      event.preventDefault()
      close()
    }
  }

  /* Picking from a grid puts the caret back where the typing happens, so the two ways in
     do not feel like separate controls. */
  const pick = (nextHour, nextMinute, done) => {
    emit(nextHour, nextMinute)
    setDraft(`${nextHour}:${nextMinute}`)
    if (done) {
      close()
      inputRef.current?.focus()
    }
  }

  return (
    <div className={`timepicker timepicker--${variant} ${className}`.trim()} ref={rootRef}>
      <div
        className={`timepicker__control${open ? ' is-open' : ''}${invalid ? ' is-invalid' : ''}${
          disabled ? ' is-disabled' : ''
        }`}
      >
        {variant === 'field' && (
          <Clock className="timepicker__icon" size={16} strokeWidth={1.75} aria-hidden="true" />
        )}
        <input
          ref={inputRef}
          id={id}
          type="text"
          className="timepicker__input"
          value={draft}
          onChange={onType}
          onFocus={() => {
            setEditing(true)
            setOpen(true)
          }}
          onBlur={commit}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          /* Digits with a colon — the numeric keypad, and the value stays LTR inside an
             RTL form because a clock time is not Arabic text. */
          inputMode="numeric"
          autoComplete="off"
          dir="ltr"
          maxLength={5}
          {...rest}
        />
      </div>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={kit.time}
            className="timepicker__panel"
            style={panelStyle(pos)}
            /* The box keeps the caret when a cell is pressed; without this the blur
               fires first and the click lands on a panel that is already closing. */
            onMouseDown={(event) => event.preventDefault()}
          >
            <div className="timepicker__column">
              <p className="timepicker__label">{kit.hours}</p>
              <div className="timepicker__grid timepicker__grid--hours">
                {HOURS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    className={`timepicker__cell${h === hour ? ' is-selected' : ''}`}
                    aria-pressed={h === hour}
                    onClick={() => pick(h, minute || '00', false)}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>

            <div className="timepicker__rule" aria-hidden="true" />

            <div className="timepicker__column">
              <p className="timepicker__label">{kit.minutes}</p>
              <div className="timepicker__grid timepicker__grid--minutes">
                {minutes.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`timepicker__cell${m === minute ? ' is-selected' : ''}`}
                    aria-pressed={m === minute}
                    onClick={() => pick(hour || '09', m, true)}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
